import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader,
  Save,
  Send,
  CheckCircle,
  AlertCircle,
  ClipboardCheck,
} from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import securityApi from '../../api/securityApi';
import toast from 'react-hot-toast';
import { useScan } from '../../contexts/ScanContext';
import { isAdminUser } from '../../utils/auth';
import PolicyStatusBadge from '../../components/compliance/policies/PolicyStatusBadge';
import ClaimVerificationSidebar from '../../components/compliance/policies/ClaimVerificationSidebar';

const PolicyEditor = () => {
  const { projectId, policyId } = useParams();
  const navigate = useNavigate();
  const { user } = useScan();

  const [policy, setPolicy] = useState(null);
  const [markdownContent, setMarkdownContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [claims, setClaims] = useState([]);
  const [claimsSummary, setClaimsSummary] = useState(null);
  const editorRef = useRef(null);

  const hasUnsavedChanges = markdownContent !== savedContent;
  const isAdmin = isAdminUser(user);

  const fetchPolicy = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await securityApi.getProjectPolicy(projectId, policyId);
      setPolicy(data);
      setMarkdownContent(data.content || '');
      setSavedContent(data.content || '');
    } catch (err) {
      console.error('Error fetching policy:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load policy');
    } finally {
      setLoading(false);
    }
  }, [projectId, policyId]);

  const fetchClaims = useCallback(async () => {
    try {
      const data = await securityApi.listPolicyClaims(projectId, policyId);
      setClaims(data.claims || []);
      setClaimsSummary(data);
    } catch (err) {
      console.error('Error fetching claims:', err);
    }
  }, [projectId, policyId]);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  // Warn about unsaved changes on page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmed) return;
    }
    navigate(`/compliance/projects/${projectId}`);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const updated = await securityApi.updateProjectPolicy(projectId, policyId, {
        content: markdownContent,
      });
      setPolicy(updated);
      setSavedContent(markdownContent);
      toast.success('Draft saved');
      fetchClaims(); // Re-fetch claims after save (backend re-extracts)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    setSubmitting(true);
    try {
      // Save content first, then change status
      const updated = await securityApi.updateProjectPolicy(projectId, policyId, {
        content: markdownContent,
        status: 'review',
      });
      setPolicy(updated);
      setSavedContent(markdownContent);
      toast.success('Policy submitted for review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit for review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      const updated = await securityApi.approveProjectPolicy(projectId, policyId);
      setPolicy(updated);
      toast.success('Policy approved');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error?.includes('unverified claims')) {
        toast.error(`${detail.message} (${detail.unverified_claims_count} unverified)`);
        setSidebarOpen(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to approve policy');
      }
    } finally {
      setApproving(false);
    }
  };

  const handleFindInPolicy = (claimText) => {
    // Search for the claim text in the policy content using first 50 chars
    const searchText = claimText.slice(0, 50);
    const index = markdownContent.toLowerCase().indexOf(searchText.toLowerCase());
    setSidebarOpen(false);

    if (index === -1) {
      toast.error('Could not locate the exact text. The claim may have been rephrased by the LLM.');
      return;
    }

    // MDEditor uses a textarea with class 'w-md-editor-text-input'.
    // Wait for sidebar to close, then find it, focus, select, and scroll.
    setTimeout(() => {
      const textarea = document.querySelector('.w-md-editor-text-input');
      if (!textarea) {
        toast.error('Could not access the editor. Try switching to edit mode.');
        return;
      }

      textarea.focus();
      textarea.setSelectionRange(index, index + searchText.length);

      // Scroll textarea so the selected text is visible
      const totalLines = markdownContent.split('\n').length || 1;
      const targetLine = markdownContent.slice(0, index).split('\n').length;
      const scrollRatio = Math.max(0, (targetLine - 3) / totalLines);
      textarea.scrollTop = scrollRatio * textarea.scrollHeight;

      toast.success('Claim highlighted in editor — review and edit as needed');
    }, 300);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Policy</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/compliance/projects/${projectId}`)}
            className="text-red-700 hover:text-red-800 font-medium"
          >
            Back to Project
          </button>
        </div>
      </div>
    );
  }

  const isApproved = policy?.status === 'approved';
  const isDraft = policy?.status === 'draft';
  const isReview = policy?.status === 'review';

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {policy?.title || 'Policy'}
              </h1>
              <PolicyStatusBadge status={policy?.status} />
              {policy?.version && (
                <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                  v{policy.version}
                </span>
              )}
            </div>
            {policy?.policy_type && (
              <p className="text-sm text-gray-500 mt-1">{policy.policy_type}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
              Unsaved changes
            </span>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="relative flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ClipboardCheck className="w-4 h-4" />
            Claims
            {claimsSummary && claimsSummary.unverified_count > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full">
                {claimsSummary.unverified_count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Control mapping chips */}
      {policy?.mapped_controls && policy.mapped_controls.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4 ml-14">
          {policy.mapped_controls.map((control) => (
            <span
              key={control}
              className="text-xs font-mono bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded"
            >
              {control}
            </span>
          ))}
        </div>
      )}

      {/* Markdown Editor */}
      <div className="mb-6" data-color-mode="light" ref={editorRef}>
        <MDEditor
          value={markdownContent}
          onChange={(val) => setMarkdownContent(val || '')}
          height={600}
          preview={isApproved ? 'preview' : 'live'}
          hideToolbar={isApproved}
        />
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
        <div className="text-sm text-gray-500">
          {policy?.updated_at && (
            <span>Last updated: {new Date(policy.updated_at).toLocaleString()}</span>
          )}
          {policy?.approved_by && (
            <span className="ml-4">Approved by: {policy.approved_by}</span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Save Draft - visible when not approved */}
          {!isApproved && (
            <button
              onClick={handleSaveDraft}
              disabled={saving || !hasUnsavedChanges}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Draft
            </button>
          )}

          {/* Submit for Review - visible when draft */}
          {isDraft && (
            <button
              onClick={handleSubmitForReview}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {submitting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Submit for Review
            </button>
          )}

          {/* Approve - visible for admins when in review, disabled if unsaved changes */}
          {isReview && isAdmin && (
            <button
              onClick={handleApprove}
              disabled={approving || hasUnsavedChanges}
              title={hasUnsavedChanges ? 'Save your changes before approving' : undefined}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {approving ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Approve
            </button>
          )}
        </div>
      </div>

      <ClaimVerificationSidebar
        projectId={projectId}
        policyId={policyId}
        claims={claims}
        claimsSummary={claimsSummary}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onClaimsUpdated={fetchClaims}
        onFindInPolicy={handleFindInPolicy}
      />
    </div>
  );
};

export default PolicyEditor;
